import BreadcrumbWithLink from "components/Common/BreadcrumbWithLink";
import ConfirmModal from "components/shared-components/ConfirmModal";
import HubFilter from "components/shared-components/RemoteTable/HubFilter/HubFilter";
import RemoteDataTable from "components/shared-components/RemoteTable/data-table";
import { CUSTOM_SELECT_FILTER_TABLE_TYPE } from "constants/RemoteDataTableContanst";
import {
  useClearRunnerScheduleChanges,
  useListAllocateRunner,
  useListRoute,
  useListRunnerUser,
  useRunnerScheduleWeeksByHub,
  useTimeSlotsV2,
} from "hooks";
import jwt_decode from "jwt-decode";

import { ROLE_APP } from "constants/actionAccess";
import { convertDateTime, formatDateTime } from "helpers/utils";
import { getMondayOfWeekDateString } from "helpers/weekScheduleHelper";
import { getInitRouteParams } from "pages/RouteManagement/utils";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { customFilter } from "react-bootstrap-table2-filter";
import { useTranslation } from "react-i18next";
import { MetaTags } from "react-meta-tags";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Button,
  Card,
  CardBody,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Spinner,
} from "reactstrap";
import { AUTH_TOKEN } from "store/auth/login/actionTypes";
import "./AllocateRunner.scss";
import AllocateRunnerDetail from "./AllocateRunnerDetail";
import ScheduleRunner from "./ListScheduleChangeRunner";
import PriorityScheduleModal from "pages/PrioritySchedule/PriorityScheduleModal";
import { getInitAllocateRunnerParams } from "./utils";

function normalizeRunnerWeeksPayload(apiData) {
  if (!apiData) {
    return [];
  }
  if (Array.isArray(apiData)) {
    return apiData;
  }
  if (Array.isArray(apiData.items)) {
    return apiData.items;
  }
  if (Array.isArray(apiData.weeks)) {
    return apiData.weeks;
  }
  if (Array.isArray(apiData.result)) {
    return apiData.result;
  }
  if (Array.isArray(apiData.data)) {
    return apiData.data;
  }
  if (apiData.data && typeof apiData.data === "object") {
    return normalizeRunnerWeeksPayload(apiData.data);
  }
  return [];
}

function getRunnerScheduleMeta(apiData) {
  if (!apiData || typeof apiData !== "object" || Array.isArray(apiData)) {
    return { maxWeekOfYear: null, maxLoop: null };
  }

  const maxWeekOfYear = Number(
    apiData.maxWeekOfYear ?? apiData.max_week_of_year
  );
  const maxLoop = Number(apiData.maxLoop ?? apiData.max_loop);

  return {
    maxWeekOfYear:
      Number.isFinite(maxWeekOfYear) && maxWeekOfYear > 0 ? maxWeekOfYear : null,
    maxLoop: Number.isFinite(maxLoop) && maxLoop > 0 ? maxLoop : null,
  };
}

function resolveRunnerDisplayWeekNumber(weekOfYear, loop, maxWeekOfYear, maxLoop) {
  const weekNum = Number(weekOfYear);
  const loopNum = Number(loop);

  if (
    !Number.isFinite(weekNum) ||
    weekNum <= 0 ||
    maxWeekOfYear == null ||
    maxLoop == null ||
    maxLoop <= 0 ||
    !Number.isFinite(loopNum) ||
    loopNum <= 0
  ) {
    return weekNum;
  }

  const weeksPerLoop = maxWeekOfYear / maxLoop;

  if (!Number.isFinite(weeksPerLoop) || weeksPerLoop <= 0) {
    return weekNum;
  }

  return weekNum - (loopNum - 1) * weeksPerLoop;
}

function parseRunnerWeekRow(row, scheduleMeta = {}) {
  const rawStart =
    row.week_start_date ?? row.date ?? row.week_start ?? row.day ?? "";
  const weekStart =
    typeof rawStart === "string"
      ? rawStart
      : rawStart != null
        ? String(rawStart)
        : "";
  const weekOfYear =
    row.week_of_year ?? row.week_number ?? row.week_no ?? row.week ?? "";
  const loopVal =
    row.loop ?? row.repeat_count ?? row.repeat ?? row.loop_count ?? "";
  const loop = loopVal === "" ? NaN : Number(loopVal);
  const displayWeekOfYear = resolveRunnerDisplayWeekNumber(
    weekOfYear,
    loop,
    scheduleMeta.maxWeekOfYear,
    scheduleMeta.maxLoop
  );

  return {
    week_start_date: weekStart,
    display_week_of_year: displayWeekOfYear,
    loop,
  };
}

function getRunnerLoopStartMoments(normalizedRows) {
  const byLoop = {};

  normalizedRows.forEach(row => {
    if (!Number.isFinite(row.loop) || !row.week_start_date) {
      return;
    }
    const m = moment(row.week_start_date, "YYYY-MM-DD", true);
    if (!m.isValid()) {
      return;
    }
    if (!byLoop[row.loop]) {
      byLoop[row.loop] = [];
    }
    byLoop[row.loop].push(m.clone().startOf("day"));
  });

  const loopStarts = {};
  Object.keys(byLoop).forEach(loopKey => {
    loopStarts[Number(loopKey)] = moment.min(byLoop[loopKey]);
  });

  return loopStarts;
}

function getActiveRunnerScheduleLoop(normalizedRows, todayMoment) {
  const distinctLoops = Array.from(
    new Set(
      normalizedRows
        .map(r => r.loop)
        .filter(loop => Number.isFinite(loop))
    )
  ).sort((a, b) => a - b);

  if (!distinctLoops.length) {
    return null;
  }

  const loopStarts = getRunnerLoopStartMoments(normalizedRows);
  const todayStart = todayMoment.clone().startOf("day");
  let activeLoop = distinctLoops[0];

  distinctLoops.forEach(loopNum => {
    const start = loopStarts[loopNum];
    if (start && start.isSameOrBefore(todayStart, "day")) {
      activeLoop = loopNum;
    }
  });

  return activeLoop;
}

const ListAllocateRunner = () => {
  const TABLE_FILTER_KEYS = ["filter[timeslot_id]", "filter[route_id]"];

  const { t, i18n } = useTranslation(["allocateRunner", "common", "prioritySchedule"]);
  const navigate = useNavigate();

  /* ================================== START INIT STATE ==================================== */
  const [isLogAdmin, setIsLogAdmin] = useState(false);
  const [isHubAdmin, setIsHubAdmin] = useState(true);
  const [runners, setRunners] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [columnsTable, setColumnsTable] = useState([]);
  const [allocateRunnerParams, setAllocateRunnerParams] = useState(
    getInitAllocateRunnerParams
  );
  const [isOpenDetail, setIsOpenDetail] = useState(false);
  const [dataDetail, setDataDetail] = useState({
    runnerId: "",
    hubInfo: undefined,
  });
  const [allocateRunnerList, setAllocateRunnerList] = useState([]);
  const [isOpenChangeSchedule, setIsOpenChangeSchedule] = useState(false);
  const [isOpenPrioritySchedule, setIsOpenPrioritySchedule] = useState(false);
  const togglePrioritySchedule = () => setIsOpenPrioritySchedule(prev => !prev);
  const [timeslotParams, setTimeslotParams] = useState({
    params: {
      "filter[type]": 0,
      "filter[record_status]": "O",
    },
    run: false,
  });
  const [routeParams, setRouteParams] = useState({
    params: {
      ...getInitRouteParams(),
      page: (getInitRouteParams().page = -1),
    },
    run: false,
  });
  const [isSelectAllHub, setIsSelectAllHub] = useState(true);
  const [isWeekScheduleDropdownOpen, setIsWeekScheduleDropdownOpen] = useState(false);

  /* =================================== END INIT STATE ===================================== */

  /* ===================================== START HOOKS ====================================== */

  // USER LIST

  const onFetchRunnerUserListSuccess = data => {
    setRunners(data?.items || []);
  };

  const { refetch: fetchRunner } = useListRunnerUser(
    onFetchRunnerUserListSuccess
  );

  // TIME SLOT

  const onFetchTimeSlots = response => {
    const list = response?.items || [];
    const result = [];

    list.forEach(item => {
      result.push({ label: item.name, value: item.id });
    });

    setTimeSlots(result);
  };

  const { refetch: fetchTimeSlots, isFetching: isLoadingTimeSlot } =
    useTimeSlotsV2(timeslotParams, onFetchTimeSlots);

  // ROUTE LIST

  const onFetchRouteListSuccess = data => {
    const list = data?.items || [];
    const result = [];

    list.forEach(item => {
      result.push({ label: item.code, value: item.id });
    });

    setRoutes(result);
  };

  const { refetch: fetchRoute, isFetching: isLoadingRoute } = useListRoute(
    routeParams,
    onFetchRouteListSuccess
  );

  // ALLOCATE RUNNER LIST
  const fetchAllocateRunnerListSuccess = response => {
    const items = response?.items || [];
    const cacheRow = [];

    items.forEach(item => {
      const groupingData = {
        mon: item.mon,
        tue: item.tue,
        wed: item.wed,
        thu: item.thu,
        fri: item.fri,  
        sat: item.sat,
        sun: item.sun,
        hub: item.hub,
        timeSlot: item.timeSlot,
        priorityActiveByDay: item.priorityActiveByDay,
      };

      const rowHash = hashCode(groupingData);

      const index = cacheRow.findIndex(row => row[rowHash]);

      if (index === -1) {
        const temp = {};

        temp[rowHash] = {
          ...groupingData,
          routeCode: item.route.code,
          description: item.route.description
            ? [`${item.route.code}: ${item.route.name}`]
            : [],
        };
        cacheRow.push(temp);
      } else {
        cacheRow[index][rowHash] = {
          ...cacheRow[index][rowHash],
          routeCode:
            cacheRow[index][rowHash]["routeCode"] + `, ${item.route.code}`,
          description: item.route.description
            ? [
              ...cacheRow[index][rowHash]["description"],
              `${item.route.code}: ${item.route.name}`,
            ]
            : cacheRow[index][rowHash]["description"],
        };
      }
    });

    setAllocateRunnerList(cacheRow);
  };

  const {
    isFetching: isLoadingAllocateRunnerList,
    refetch: fetchAllocateRunnerList,
  } = useListAllocateRunner(
    { params: allocateRunnerParams },
    fetchAllocateRunnerListSuccess
  );

  useEffect(() => {
    if (allocateRunnerParams && allocateRunnerParams.run) {
      fetchRunner();
      fetchAllocateRunnerList();
    }
  }, [allocateRunnerParams]);

  // START Clear RunnerSchedule
  const [modalConfirmClear, setModalConfirmClear] = useState(false);
  const toggleConfirmClearModal = () =>
    setModalConfirmClear(!modalConfirmClear);

  const onClearSuccess = () => {
    fetchAllocateRunnerList();
    toggleConfirmClearModal();
    //
  };
  const { isLoading: isLoadingClear, mutate: mutateClear } =
    useClearRunnerScheduleChanges(onClearSuccess);
  // END Clear RunnerSchedule

  useEffect(() => {
    if (timeslotParams && timeslotParams.run) {
      fetchTimeSlots();
    }
    if (routeParams && routeParams.run) {
      fetchRoute();
    }

    initColumn();
  }, [t, timeslotParams, routeParams]);

  useEffect(() => {
    initColumn();
  }, [runners, timeSlots, routes, allocateRunnerParams]);

  /* ===================================== END HOOKS ======================================== */

  /* ================================ START FUNCTION HANDLER ================================ */
  const handleSelectHub = (hubIds, isSelectAll) => {
    setIsSelectAllHub(isSelectAll);
    setIsWeekScheduleDropdownOpen(false);
    const idFilter = hubIds.map(id => parseInt(id));
    const defaultWeekStartDate = getMondayOfWeekDateString();

    setAllocateRunnerParams(prev => ({
      ...prev,
      columnFilter: {
        ...prev.columnFilter,
        "filter[week_start_date]": defaultWeekStartDate,
      },
      run: true,
    }));
    setTimeslotParams(prev => ({
      ...prev,
      params: {
        ...prev.params,
        "filter[hub_id][in]": idFilter,
      },
      run: true,
    }));
    setRouteParams(prev => ({
      ...prev,
      params: {
        ...prev.params,
        columnFilter: {
          "filter[hub_id][in]": idFilter,
        },
      },
      run: true,
    }));
  };

  const hubSchedule = useMemo(() => {
    if (allocateRunnerList && allocateRunnerList.length > 0) {
      const firstRow = JSON.parse(Object.keys(allocateRunnerList[0])[0]);
      return {
        id: firstRow?.hub?.id,
        is_center: firstRow?.hub?.is_center,
        name: firstRow?.hub?.name,
      };
    }

    return undefined;
  }, [allocateRunnerList]);

  const hubIdForWeekSchedule = useMemo(() => {
    const rawHubIds = allocateRunnerParams.columnFilter?.hub_ids;
    if (rawHubIds == null || rawHubIds === "") {
      return null;
    }

    const firstHubIdSegment = String(rawHubIds).split(",")[0]?.trim();
    const parsedHubId = parseInt(firstHubIdSegment, 10);

    return Number.isFinite(parsedHubId) ? parsedHubId : null;
  }, [allocateRunnerParams.columnFilter?.hub_ids]);

  const {
    data: runnerWeeksScheduleData,
    isFetching: isLoadingRunnerWeeksSchedule,
  } = useRunnerScheduleWeeksByHub({
    hubId: hubIdForWeekSchedule,
    enabled: Boolean(hubIdForWeekSchedule),
  });

  const selectedWeekStartDate =
    allocateRunnerParams.columnFilter?.["filter[week_start_date]"] || "";

  const handleWeekStartDateSelect = useCallback(weekStartDateIso => {
    if (!weekStartDateIso) {
      return;
    }

    setAllocateRunnerParams(prev => ({
      ...prev,
      columnFilter: {
        ...prev.columnFilter,
        "filter[week_start_date]": weekStartDateIso,
      },
      run: true,
    }));
    setIsWeekScheduleDropdownOpen(false);
  }, []);

  const runnerScheduleMeta = useMemo(
    () => getRunnerScheduleMeta(runnerWeeksScheduleData),
    [runnerWeeksScheduleData]
  );

  const normalizedRunnerWeekRows = useMemo(() => {
    const raw = normalizeRunnerWeeksPayload(runnerWeeksScheduleData);

    return raw.map(row => parseRunnerWeekRow(row, runnerScheduleMeta)).filter(row => {
      const hasLoop = Number.isFinite(row.loop);
      const hasDate =
        typeof row.week_start_date === "string" &&
        row.week_start_date.length > 0;

      return hasLoop && hasDate;
    });
  }, [runnerWeeksScheduleData, runnerScheduleMeta]);

  const runnerWeekScheduleSlice = useMemo(() => {
    if (!normalizedRunnerWeekRows.length) {
      return {
        activeLoop: null,
        activeWeeks: [],
        loopStartLabel: "",
      };
    }

    const activeLoop = getActiveRunnerScheduleLoop(normalizedRunnerWeekRows, moment());
    const loopStarts = getRunnerLoopStartMoments(normalizedRunnerWeekRows);
    const loopStartMoment =
      activeLoop != null ? loopStarts[activeLoop] : null;

    const activeWeeks = normalizedRunnerWeekRows
      .filter(row => row.loop === activeLoop)
      .sort((a, b) =>
        moment(a.week_start_date, "YYYY-MM-DD").diff(
          moment(b.week_start_date, "YYYY-MM-DD")
        )
      );

    const loopStartLabel =
      loopStartMoment &&
      convertDateTime(
        loopStartMoment.format("YYYY-MM-DD"),
        formatDateTime.DD_MM_YYYY,
        "/"
      );

    return {
      activeLoop,
      activeWeeks,
      loopStartLabel: loopStartLabel || "",
    };
  }, [normalizedRunnerWeekRows]);

  const weekScheduleToggleLabel = useMemo(() => {
    if (isLoadingRunnerWeeksSchedule) {
      return "Đang tải...";
    }

    const { activeWeeks, loopStartLabel } = runnerWeekScheduleSlice;
    const isoForLabel =
      selectedWeekStartDate ||
      activeWeeks[0]?.week_start_date ||
      "";

    if (!isoForLabel) {
      return loopStartLabel ? `Tuần: ${loopStartLabel}` : "—";
    }

    const labelDate =
      convertDateTime(isoForLabel, formatDateTime.DD_MM_YYYY, "/") ||
      loopStartLabel ||
      "—";
    const selectedWeekRow =
      activeWeeks.find(row => row.week_start_date === isoForLabel) ||
      normalizedRunnerWeekRows.find(row => row.week_start_date === isoForLabel);
    const weekNumber = selectedWeekRow?.display_week_of_year;

    if (weekNumber != null && weekNumber !== "") {
      return `Tuần ${weekNumber}: ${labelDate}`;
    }

    return `Tuần: ${labelDate}`;
  }, [
    selectedWeekStartDate,
    runnerWeekScheduleSlice,
    normalizedRunnerWeekRows,
    isLoadingRunnerWeeksSchedule,
  ]);

  const weekScheduleMenuWeeks = runnerWeekScheduleSlice.activeWeeks;
  const hasWeekScheduleOptions = weekScheduleMenuWeeks.length > 0;

  const handleClearSchedule = async () => {
    if (hubSchedule) {
      mutateClear(hubSchedule.id);
    }
  };

  const handleTableChange = (
    type,
    { page, sizePerPage, sortField, sortOrder, columnFilter }
  ) => {
    const tableColumnFilter = columnFilter || {};
    setAllocateRunnerParams(prev => {
      const fallbackWeekStartDate = prev.columnFilter?.["filter[week_start_date]"] || "";
      const persistentColumnFilter = {
        ...prev.columnFilterKeep,
        ...(fallbackWeekStartDate
          ? { "filter[week_start_date]": fallbackWeekStartDate }
          : {}),
      };
      const nextColumnFilter = {
        ...persistentColumnFilter,
        ...tableColumnFilter,
      };

      TABLE_FILTER_KEYS.forEach(filterKey => {
        const value = nextColumnFilter[filterKey];
        const isEmptyValue = value == null || value === "";
        if (isEmptyValue) {
          delete nextColumnFilter[filterKey];
        }
      });

      return {
        ...prev,
        sort: sortField ? sortOrder + sortField : allocateRunnerParams.sort,
        columnFilter: nextColumnFilter,
        page: -1,
        sizePerPage: sizePerPage || 10,
      };
    });
  };

  const initColumn = () => {
    setColumnsTable(initColumns);
  };

  const rowEventsHandler = {
    onDoubleClick: (_, row) => { },
  };

  const optionShowColumnHandler = (field, value) => { };

  const onSearchAllHandle = value => {
    setAllocateRunnerParams(prev => ({
      ...prev,
      keywords: value,
    }));
  };

  const generateId = (data = []) => {
    const result = [];
    data.forEach((item, index) => {
      result.push({ ...item, id: index });
    });
    console.log("🚀 ~ generateId ~ result:", result);
    return result;
  };

  const normalizeRunnerIds = dayData => {
    if (Array.isArray(dayData)) {
      return dayData;
    }

    if (typeof dayData === "number") {
      return [dayData];
    }

    if (typeof dayData === "string") {
      const trimmedDayData = dayData.trim();
      if (!trimmedDayData) {
        return [];
      }

      if (trimmedDayData.startsWith("[") && trimmedDayData.endsWith("]")) {
        try {
          const parsedDayData = JSON.parse(trimmedDayData);
          return Array.isArray(parsedDayData) ? parsedDayData : [];
        } catch (error) {
          return [];
        }
      }

      if (trimmedDayData.includes(",")) {
        return trimmedDayData.split(",").map(item => item.trim());
      }

      return [trimmedDayData];
    }

    return [];
  };

  const getRowData = row => {
    const dataKey = Object.keys(row || {}).find(key => key !== "id");
    return dataKey ? row[dataKey] : undefined;
  };

  const renderRunnerName = (dayData, hubInfo) => {
    const runnerIds = normalizeRunnerIds(dayData);
    let runnersDisplay = [];

    runnerIds.forEach(runnerId => {
      const normalizedRunnerId = Number(runnerId);
      const runner = runners.find(
        runner => Number(runner.id) === normalizedRunnerId
      );
      if (!!runner) {
        runnersDisplay.push(runner);
      }
    });

    return runnersDisplay.map((runner, index) => {
      return (
        <div key={index} className="allocate_runner__content_allocate_runner">
          <span
            onClick={() => onOpenDetail(runner, hubInfo)}
            className="display_runner_name"
          >
            {runner.display_name}
          </span>
        </div>
      );
    });
  };

  const renderRunnerNameContainer = ({ row, day }) => {
    const rowData = getRowData(row);
    if (!rowData) {
      return null;
    }

    const runnerName = renderRunnerName(rowData[day], rowData?.hub);
    return (
      <div className="d-flex align-items-center justify-content-between">
        <div>{runnerName}</div>
      </div>
    );
  };

  const hashCode = value => {
    return JSON.stringify(value);
  };

  /* ================================ END FUNCTION HANDLER ================================= */

  // DETAIL MODAL

  const toggleDetail = () => {
    setIsOpenDetail(!isOpenDetail);
  };

  const onOpenDetail = (runner, hubInfo) => {
    setDataDetail({ runnerId: runner.id, hubInfo });
    toggleDetail();
  };

  const toggleChangeSchedule = () => {
    setIsOpenChangeSchedule(!isOpenChangeSchedule);
  };

  const getPriorityActiveStyle = (row, day) => {
    const rowData = getRowData(row);
    const isPriorityActive = rowData?.priorityActiveByDay?.[day];
    if (isPriorityActive) {
      return { backgroundColor: "#ffebee" };
    }
    return {};
  };

  const initColumns = [
    {
      dataField: "mon",
      text: t("table.mon"),
      sort: false,
      allowExport: true,
      align: "center",
      style: (cell, row) => getPriorityActiveStyle(row, "mon"),
      formatter: (cell, row) => {
        return renderRunnerNameContainer({ row: row, day: "mon" });
      },
    },
    {
      dataField: "tue",
      text: t("table.tue"),
      sort: false,
      allowExport: true,
      align: "center",
      style: (cell, row) => getPriorityActiveStyle(row, "tue"),
      formatter: (cell, row) => {
        return renderRunnerNameContainer({ row: row, day: "tue" });
      },
    },
    {
      dataField: "wed",
      text: t("table.wed"),
      sort: false,
      allowExport: true,
      align: "center",
      style: (cell, row) => getPriorityActiveStyle(row, "wed"),
      formatter: (cell, row) => {
        return renderRunnerNameContainer({ row: row, day: "wed" });
      },
    },
    {
      dataField: "thu",
      text: t("table.thu"),
      sort: false,
      allowExport: true,
      align: "center",
      style: (cell, row) => getPriorityActiveStyle(row, "thu"),
      formatter: (cell, row) => {
        return renderRunnerNameContainer({ row: row, day: "thu" });
      },
    },
    {
      dataField: "fri",
      text: t("table.fri"),
      sort: false,
      align: "center",
      allowExport: true,
      style: (cell, row) => getPriorityActiveStyle(row, "fri"),
      formatter: (cell, row) => {
        return renderRunnerNameContainer({ row: row, day: "fri" });
      },
    },
    {
      dataField: "sat",
      text: t("table.sat"),
      sort: false,
      allowExport: true,
      align: "center",
      style: (cell, row) => getPriorityActiveStyle(row, "sat"),
      formatter: (cell, row) => {
        return renderRunnerNameContainer({ row: row, day: "sat" });
      },
    },
    {
      dataField: "sun",
      text: t("table.sun"),
      sort: false,
      allowExport: true,
      align: "center",
      style: (cell, row) => getPriorityActiveStyle(row, "sun"),
      formatter: (cell, row) => {
        return renderRunnerNameContainer({ row: row, day: "sun" });
      },
    },
    {
      dataField: "hub_id",
      text: "Center/Hub",
      sort: true,
      formatter: (cell, row) => {
        return <span>{row[Object.keys(row)[0]]?.hub?.name}</span>;
      },
    },
    {
      dataField: "timeslot_id",
      text: t("table.time_slot"),
      sort: false,
      allowExport: true,
      align: "center",
      filter: customFilter({
        type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => {
        return (
          <div style={{ minWidth: "100px", fontWeight: "normal" }}>
            <Select
              isClearable
              placeholder={t("common:placeholder.select", {
                title: t("table.time_slot"),
              })}
              options={timeSlots || []}
              classNamePrefix="select2-selection"
              value={selectedTimeSlot}
              isLoading={isLoadingTimeSlot}
              onChange={data => {
                onFilter({ data: data });
                setSelectedTimeSlot(data);
              }}
            />
          </div>
        );
      },
      formatter: (cell, row) => {
        return <span>{row[Object.keys(row)[0]]?.timeSlot?.name}</span>;
      },
    },
    {
      dataField: "route_id",
      text: t("table.route_code"),
      sort: false,
      allowExport: true,
      align: "center",
      filter: customFilter({
        type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => {
        return (
          <div style={{ minWidth: "100px", fontWeight: "normal" }}>
            <Select
              isClearable
              placeholder={t("common:placeholder.select", {
                title: t("table.route_code"),
              })}
              isLoading={isLoadingRoute}
              options={routes || []}
              classNamePrefix="select2-selection"
              value={selectedRoute}
              onChange={data => {
                onFilter({ data: data });
                setSelectedRoute(data);
              }}
            />
          </div>
        );
      },
      formatter: (cell, row) => {
        return <span>{row[Object.keys(row)?.[0]]?.["routeCode"]}</span>;
      },
      title: (cell, row, rowIndex, colIndex) => {
        let descriptionValue = "";
        const descriptions = row[Object.keys(row)?.[0]]?.["description"] || [];

        descriptions.forEach(item => {
          descriptionValue += `\n ${item} \n`;
        });

        return descriptionValue;
      },
    },
  ];

  useEffect(() => {
    const info = jwt_decode(localStorage.getItem(AUTH_TOKEN));
    const listRole = info["auth"].split(",");
    setIsHubAdmin(
      listRole.find(item => item.includes(ROLE_APP.ROLE_HUB_ADMIN))
        ? true
        : false || false
    );
    setIsLogAdmin(
      listRole.find(item => item.includes(ROLE_APP.ROLE_LOG_ADMIN))
        ? true
        : false || false
    );
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags ch>
          <title>{t("title.list")}</title>
        </MetaTags>
        <div className="container-fluid">
          <BreadcrumbWithLink
            title={`${t("title.list")} (${allocateRunnerList?.length || 0})`}
            breadcrumbItems={[
              { name: t("title.allocate_runner") },
              { name: t("title.list") },
            ]}
          />

          <Row>
            <Col className="col-12">
              <AllocateRunnerDetail
                isOpenModal={isOpenDetail}
                toggleModal={toggleDetail}
                dataDetail={dataDetail}
              />
            </Col>
            <Col className="col-12">
              <ScheduleRunner
                isOpenModal={isOpenChangeSchedule}
                toggleModal={toggleChangeSchedule}
              />
            </Col>
            <Col className="col-12">
              <PriorityScheduleModal
                isOpen={isOpenPrioritySchedule}
                toggle={togglePrioritySchedule}
                userType="runner"
              />
            </Col>
          </Row>

          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  {/* Clear Modal */}
                  <ConfirmModal
                    isOpen={modalConfirmClear}
                    titleHeader={
                      <>
                        Xác nhận clear lịch phân bổ runner:{" "}
                        <span className="text-danger">
                          {hubSchedule?.is_center ? "Center" : "Hub"}{" "}
                          {hubSchedule?.name}
                        </span>
                      </>
                    }
                    titleBody={
                      "Hệ thống sẽ thực hiện clear lịch phân bổ runner ?"
                    }
                    isDisableButton={isLoadingClear}
                    onConfirm={handleClearSchedule}
                    textBtnConfirm={"Xác nhận"}
                    textBtnCancel={"Đóng"}
                    toggle={toggleConfirmClearModal}
                  />
                  {columnsTable.length > 0 && (
                    <RemoteDataTable
                      keyField="id"
                      data={generateId(allocateRunnerList)}
                      columns={columnsTable}
                      page={1}
                      sizePerPage={1}
                      totalSize={0}
                      onTableChange={handleTableChange}
                      onSearchAllHandle={onSearchAllHandle}
                      optionShowColumnHandel={optionShowColumnHandler}
                      rowEvents={rowEventsHandler}
                      listMultipleRowAction={[]}
                      listRowAction={[]}
                      loading={isLoadingAllocateRunnerList}
                      isShowSearchIndex={false}
                      isShowPagination={false}
                    >
                      <div className="d-flex gap-2 flex-wrap align-items-center">
                        <HubFilter
                          mainFilterParams={allocateRunnerParams}
                          setMainFilterParams={setAllocateRunnerParams}
                          isNotFilterChildHub={true}
                          isHaveAllOption={true}
                          isFilterAllDefault={true}
                          handleSelectHub={handleSelectHub}
                        />
                        <div className="allocate-runner-week-dropdown">
                          <Dropdown
                            isOpen={isWeekScheduleDropdownOpen}
                            toggle={() => setIsWeekScheduleDropdownOpen(prev => !prev)}
                          >
                            <DropdownToggle color="primary" caret className="px-3">
                              {weekScheduleToggleLabel}
                            </DropdownToggle>
                            <DropdownMenu className="py-2 px-0">
                              {isLoadingRunnerWeeksSchedule && (
                                <DropdownItem
                                  disabled
                                  toggle={false}
                                  className="py-2 px-3 text-muted"
                                >
                                  <Spinner size="sm" className="me-2 align-middle" />
                                  Đang tải...
                                </DropdownItem>
                              )}
                              {!isLoadingRunnerWeeksSchedule && hasWeekScheduleOptions
                                && weekScheduleMenuWeeks.map(row => {
                                  const labelDate = convertDateTime(
                                    row.week_start_date,
                                    formatDateTime.DD_MM_YYYY,
                                    "/"
                                  );
                                  const isSelected =
                                    selectedWeekStartDate &&
                                    row.week_start_date === selectedWeekStartDate;

                                  return (
                                    <DropdownItem
                                      key={`${row.loop}-${row.week_start_date}`}
                                      toggle={false}
                                      active={Boolean(isSelected)}
                                      className="py-2 px-3"
                                      onClick={() => handleWeekStartDateSelect(row.week_start_date)}
                                    >
                                      Tuần {row.display_week_of_year}: {labelDate}
                                    </DropdownItem>
                                  );
                                })}
                              {!isLoadingRunnerWeeksSchedule && !hasWeekScheduleOptions && (
                                <DropdownItem
                                  disabled
                                  toggle={false}
                                  className="py-2 px-3 text-muted"
                                >
                                  {t("common:message.no_options")}
                                </DropdownItem>
                              )}
                            </DropdownMenu>
                          </Dropdown>
                        </div>
                   
                        {isLogAdmin && (
                          <>
                            <Button
                              onClick={() => {
                                navigate(
                                  { pathname: "/import-management/import" },
                                  {
                                    state: { isFocusTransport: true, code: 50 },
                                  }
                                );
                              }}
                              color="primary"
                              className="btn-label"
                              type="button"
                            >
                              <i className="fas fa-file-upload me-2 label-icon " />
                              {t("title.import")}
                            </Button>
                            <Button
                              onClick={() => {
                                toggleChangeSchedule();
                              }}
                              color="success"
                              className="btn-label"
                              type="button"
                            >
                              <i className="bx bxs-calendar me-2 label-icon" />
                              Đổi lịch
                            </Button>
                            <Button
                              onClick={() => {
                                toggleConfirmClearModal();
                              }}
                              color="danger"
                              className="btn-label"
                              type="button"
                              disabled={!hubSchedule || isSelectAllHub}
                            >
                              <i className="bx bxs-trash me-2 label-icon" />
                              Clear lịch phân bổ runner
                            </Button>
                            <Button
                          onClick={togglePrioritySchedule}
                          color="warning"
                          className="btn-label"
                          type="button"
                        >
                          <i className="bx bx-star me-2 label-icon" />
                          {t("prioritySchedule:button.open")}
                        </Button>
                          </>
                        )}
                      </div>
                    </RemoteDataTable>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ListAllocateRunner;
